import { parseNumber, parseString } from 'utils/parsingHelpers'

export enum ResponsibleShop {
  PIT = 'PIT',
  HOU = 'HOU',
  UAE = 'UAE',
}

export enum RobotType {
  TOKA_5 = 'toka_5',
  TOKA_4 = 'toka_4',
  EMAT = 'emat',
  MFL = 'mfl',
  VACUUM_BOX = 'vacuum_box',
  HANDHELD_UT = 'hhut',
  NAVIC = 'navic',
  INSPIRED_FLIGHT_IF1200A = 'inspired_flight_if1200a',
  DJI_INNOVATIONS_MATRICE_350_RTK = 'dji_innovations_matrice_350_rtk',
  DJI_INNOVATIONS_MAVIC_3T = 'dji_innovations_mavic_3t',
  FREEFLY_SYSTEMS_ASTRO_MAX_BLUE = 'freefly_systems_astro_max_blue',
  FREEFLY_SYSTEMS_ASTRO = 'freefly_systems_astro',
  DJI_INNOVATIONS_MAVIC_3E = 'dji_innovations_mavic_3e',
  DJI_INNOVATIONS_MINI_3 = 'dji_innovations_mini_3',
  FLYABILITY_ELIOS_3 = 'flyability_elios_3',
  PEC_SUCTION_SYSTEM = 'pec_suction_system',
}

export const ROBOT_TYPE_DISPLAY_NAMES: Record<RobotType, string> = {
  [RobotType.TOKA_5]: 'TOKA 5',
  [RobotType.TOKA_4]: 'TOKA 4',
  [RobotType.EMAT]: 'EMAT',
  [RobotType.MFL]: 'MFL',
  [RobotType.VACUUM_BOX]: 'Vacuum Box',
  [RobotType.HANDHELD_UT]: 'Handheld UT',
  [RobotType.NAVIC]: 'Navic',
  [RobotType.INSPIRED_FLIGHT_IF1200A]: 'INSPIRED FLIGHT / IF1200A',
  [RobotType.DJI_INNOVATIONS_MATRICE_350_RTK]:
    'DJI INNOVATIONS / MATRICE 350 RTK',
  [RobotType.DJI_INNOVATIONS_MAVIC_3T]: 'DJI INNOVATIONS / MAVIC 3T',
  [RobotType.FREEFLY_SYSTEMS_ASTRO_MAX_BLUE]:
    'FREEFLY SYSTEMS / ASTRO Max Blue',
  [RobotType.FREEFLY_SYSTEMS_ASTRO]: 'FREEFLY SYSTEMS / ASTRO',
  [RobotType.DJI_INNOVATIONS_MAVIC_3E]: 'DJI INNOVATIONS / MAVIC 3E',
  [RobotType.DJI_INNOVATIONS_MINI_3]: 'DJI INNOVATIONS / Mini 3',
  [RobotType.FLYABILITY_ELIOS_3]: 'Flyability / Elios 3',
  [RobotType.PEC_SUCTION_SYSTEM]: 'PEC - Suction System',
}

const normalizeRobotTypeValue = (value: string) =>
  value.trim().toLowerCase().replace(/[\s-]+/g, '_')

export const getRobotTypeDisplayName = (
  value: RobotType | string | undefined
): string | undefined => {
  if (!value) return undefined

  const robotType = Object.values(RobotType).find(
    (candidate) =>
      normalizeRobotTypeValue(candidate) === normalizeRobotTypeValue(value)
  )

  return robotType ? ROBOT_TYPE_DISPLAY_NAMES[robotType] : undefined
}

export enum EquipmentConfig {
  CAMBER = 'camber',
  THREE_VIM = '3_vim',
  SD_THIN = 'sd_thin',
  SD_TANK = 'sd_tank',
  SD_THICK = 'sd_thick',
  TANK_SHELL = 'tank_shell',
  TANK_FLOOR = 'tank_floor',
  LARGE_TUBE = 'large_tube',
  SMALL_TUBE = 'small_tube',
  CROWN_RIDING = 'crown_riding',
  PAUT = 'paut',
  RAUT = 'raut',
  TRI_LAT = 'tri_lat',
  NO_CONFIG = 'no_config',
  PAYLOAD_SONY_A7R_MK4 = 'payload_sony_a7r_mk4',
  PAYLOAD_SONY_ILX_LR1 = 'payload_sony_ilx_lr1',
  PAYLOAD_PHASE_ONE_P3 = 'payload_phase_one_p3',
  PAYLOAD_DJI_INNOVATIONS_ZENMUSE_P1 = 'payload_dji_innovations_zenmuse_p1',
  PAYLOAD_DJI_INNOVATIONS_ZENMUSE_H30T = 'payload_dji_innovations_zenmuse_h30t',
}

export enum ShippingMethods {
  CARRY_WITH_CREW = 'carry',
  FREIGHT_CARGO = 'freight_cargo',
}

export interface EquipmentDetailJSON {
  detail_id: string
  robot_type: RobotType
  num_robots: number
  num_spare_robots: number
  equipment_config: EquipmentConfig[]
  robot_config?: string
  responsible_shop: ResponsibleShop[]
  cal_block_requirements?: string
  shipping_methods: ShippingMethods
  num_spare_laptops: number
  air_way_bill?: string
}

export const getResponsibleShopArray = (
  values: string[]
): ResponsibleShop[] => {
  if (
    values.every((value) =>
      Object.values(ResponsibleShop).includes(value as ResponsibleShop)
    )
  ) {
    return values as ResponsibleShop[]
  }
  return []
}

export const getRobotType = (value: string | undefined) => {
  if (Object.values(RobotType).includes(value as RobotType)) {
    return value as RobotType
  }
  return RobotType.TOKA_5
}

export const getEquipmentConfigArray = (
  values: string[]
): EquipmentConfig[] => {
  if (
    values.every((value) =>
      Object.values(EquipmentConfig).includes(value as EquipmentConfig)
    )
  ) {
    return values as EquipmentConfig[]
  }
  return []
}

export const getShippingMethod = (value: string | undefined) => {
  if (Object.values(ShippingMethods).includes(value as ShippingMethods)) {
    return value as ShippingMethods
  }
  return ShippingMethods.CARRY_WITH_CREW
}

export const parseEquipmentDetailJSON = (
  value: unknown
): EquipmentDetailJSON | undefined => {
  if (!value || typeof value !== 'object') return undefined

  const data = value as Record<string, unknown>
  return {
    detail_id: parseString(data.detail_id) ?? '',
    robot_type: getRobotType(parseString(data.robot_type)),
    num_robots: parseNumber(data.num_robots) ?? 0,
    num_spare_robots: parseNumber(data.num_spare_robots) ?? 0,
    equipment_config: getEquipmentConfigArray(
      Array.isArray(data.equipment_config)
        ? data.equipment_config.map((x) => String(x))
        : []
    ),
    robot_config: parseString(data.robot_config),
    responsible_shop: getResponsibleShopArray(
      Array.isArray(data.responsible_shop)
        ? data.responsible_shop.map((x) => String(x))
        : []
    ),
    cal_block_requirements: parseString(data.cal_block_requirements),
    shipping_methods: getShippingMethod(parseString(data.shipping_methods)),
    num_spare_laptops: parseNumber(data.num_spare_laptops) ?? 0,
    air_way_bill: parseString(data.air_way_bill),
  }
}
